import { WebStorage } from 'proxy-storage'
import store from 'utils/store/withMiddleware'
import { sendLead } from '..'
import { EnrollmentPolicy, PlanPolicy } from '../types'
import { CRM_MIDDLEWARE } from 'core/constants/endpoints'
import { removeSalesforceData } from './removeSalesforceData'
import { getCoverageType, productNames } from '../opportunity/getCoverageType'
import { fetchJSON, getISODate, stringToDate } from '@hc-frontend/utils'
import { getCarrierName } from '../utils'

const sessionStorage = new WebStorage('sessionStorage')

function mergeAddonData(addons = [], agentId = '', isTest): PlanPolicy[] {
  const state = store.getState()
  const enrollment = state.enrollment
  const appPid = enrollment ? enrollment.appPid : ''
  const orderId = enrollment ? enrollment.orderId : ''

  const addonsData = []

  addons.forEach(addon => {
    const addOnId = sessionStorage.getItem(
      `pivotSFAddOn-${productNames[addon.productId]}`
    )
    const premium =
      addon.durations && addon.durations.length > 0
        ? addon.durations[0].monthly.premium
        : ''

    if (addOnId) {
      const addOnPolicy = getPolicyFromEnrollment(enrollment, addon.planId)

      addonsData.push({
        isATest__c: isTest,
        planId: addon.planId,
        planName: addon.planName,
        product: productNames[addon.productId],
        monthlyCost: addon.premium || premium,
        idOpportunity: addOnId,
        view_TYP: true,
        app_id: addOnPolicy ? addOnPolicy.appId : undefined,
        ssn: addOnPolicy ? addOnPolicy.ssn : undefined,
        app_pid: appPid,
        order_id: orderId,
        coverageType: getCoverageType(productNames[addon.productId]),
        'User who won the opportunity': agentId
      })
    }
  })

  return addonsData
}

function makePayloadPolicy({ hcStorage, enrollment }): PlanPolicy[] | undefined {
  const plan = hcStorage.pivotPlan
  const opportunityId = sessionStorage.getItem(`pivotSFOpportunity-${plan.planId}`)

  if (!opportunityId) {
    return undefined
  }

  const sDate = getISODate(stringToDate(hcStorage.sdate))
  const appPid = enrollment ? enrollment.appPid : ''
  const orderId = enrollment ? enrollment.orderId : ''
  const firstPolicy = getPolicyFromEnrollment(enrollment, plan.planId)

  const isTest = String(hcStorage.agent_id).includes('TEST') ? 'Yes' : 'No'

  const mainPlan: PlanPolicy = {
    isATest__c: isTest,
    planId: plan.planId,
    planType: plan.planType,
    planName: plan.planName,
    carrier: getCarrierName(plan.carrier),
    period: hcStorage.period,
    monthlyCost: plan.monthlyCost,
    policies: hcStorage.policies,
    payment_frequency: plan.isPrepaid ? 'Prepaid' : 'Monthly',
    idOpportunity: opportunityId,
    view_TYP: true,
    app_id: firstPolicy ? firstPolicy.appId : undefined,
    app_pid: appPid,
    order_id: orderId,
    coverageType: getCoverageType(plan.product),
    product: plan.product,
    'User who won the opportunity': hcStorage.agent_id
  }

  if (plan.coinsurance) {
    mainPlan['coinsurance'] = plan.coinsurance.replace('%', '')
  }

  if (plan.deductible) {
    mainPlan['deductible'] = plan.deductible
  }

  if (plan.moop) {
    mainPlan['moop'] = plan.moop
  }

  if (sDate) {
    mainPlan['sdate'] = sDate
  }

  if (hcStorage.endDate) {
    mainPlan['endDate'] = getISODate(stringToDate(hcStorage.endDate))
  }

  if (plan.networkProvider) {
    mainPlan['network_provider'] = plan.networkProvider
  }

  if (hcStorage['periodDays']) {
    mainPlan['coverageDays'] = hcStorage['periodDays']
  }

  if (hcStorage['paymentType']) {
    mainPlan['payment_type'] = hcStorage['paymentType']
  }

  const addons = mergeAddonData(hcStorage.addOnSelected, hcStorage.agent_id, isTest)
  if (addons.length > 0) {
    mainPlan.add_on = addons.length
  }

  return [mainPlan, ...addons]
}

function closePolicy(): Promise<void> {
  const { hcStorage, enrollment } = store.getState()

  const body = makePayloadPolicy({ hcStorage, enrollment })
  if (!body) return undefined

  return fetchJSON<void>(`${CRM_MIDDLEWARE}/policy`, {
    body
  }).then(response => response.parsedBody)
}

function getPolicyFromEnrollment(
  enrollment,
  planId,
  policyNumber = 1
): EnrollmentPolicy | undefined {
  if (!enrollment) return undefined
  const policies = enrollment.policies || []

  return policies.find(
    policy => policy.planId === planId && policy.consecutivePolicyNumber === policyNumber
  )
}

function sendPolicy(): Promise<void> {
  const { hcStorage } = store.getState()
  if (
    !hcStorage.contact ||
    !hcStorage.contact.email_address ||
    !hcStorage.pivotPlan ||
    !hcStorage.members ||
    !hcStorage.members[0]
  ) {
    return
  }

  return sendLead().then(closePolicy).then(removeSalesforceData)
}

export { sendPolicy, closePolicy, makePayloadPolicy }
